import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import zeiterfassungService from '../services/zeiterfassungService';
import { Icon, PageHeader, LoadingScreen, EmptyState, fehlerText } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { ArbeitszeitAuswertung, Korrekturantrag, StempelTyp, TagesAuswertung, ZeiterfassungEintrag } from '../types';
import { UhrzeitFeld } from '../components/DatumZeit';

const iso = (d: Date) => d.toLocaleDateString('sv-SE');

// Montag der Woche, in der `d` liegt
const montag = (d: Date) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mo=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmt = (min: number) => `${Math.floor(Math.abs(min) / 60)}h ${(Math.abs(min) % 60).toString().padStart(2, '0')}m`;
const fmtSaldo = (min: number) => (min > 0 ? `+${fmt(min)}` : min < 0 ? `−${fmt(min)}` : '0h 00m');
const saldoColor = (min: number) => (min > 0 ? 'text-green-600' : min < 0 ? 'text-red-600' : 'text-gray-500');

const quelleLabel: Record<string, string> = { QR: 'QR-Scan', ADMIN: 'Admin-Korrektur', ANTRAG: 'Nachtrag (genehmigt)' };

const antragStatusBadge: Record<Korrekturantrag['status'], { label: string; cls: string }> = {
  OFFEN: { label: 'Antrag offen', cls: 'bg-yellow-100 text-yellow-800' },
  GENEHMIGT: { label: 'genehmigt', cls: 'bg-green-100 text-green-700' },
  ABGELEHNT: { label: 'abgelehnt', cls: 'bg-red-100 text-red-700' },
};

const statusBadge = (t: TagesAuswertung) => {
  if (t.feiertag) return { label: t.feiertag, cls: 'bg-purple-100 text-purple-700' };
  if (t.abwesenheit === 'URLAUB') return { label: 'Urlaub', cls: 'bg-blue-100 text-blue-700' };
  if (t.abwesenheit === 'KRANKHEIT') return { label: 'Krank', cls: 'bg-orange-100 text-orange-700' };
  if (t.abwesenheit) return { label: t.abwesenheit, cls: 'bg-gray-100 text-gray-700' };
  if (t.offen && t.datum < iso(new Date())) return { label: 'Abmeldung fehlt', cls: 'bg-amber-100 text-amber-700' };
  if (t.offen) return { label: 'eingestempelt', cls: 'bg-green-100 text-green-700' };
  return null;
};

interface AntragForm {
  typ: StempelTyp;
  uhrzeit: string; // HH:mm
  begruendung: string;
}

const Arbeitszeiten: React.FC = () => {
  const [wochenStart, setWochenStart] = useState(() => montag(new Date()));
  const [data, setData] = useState<ArbeitszeitAuswertung | null>(null);
  const [eintraege, setEintraege] = useState<ZeiterfassungEintrag[]>([]);
  const [antraege, setAntraege] = useState<Korrekturantrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [offenerTag, setOffenerTag] = useState<string | null>(null);
  const [form, setForm] = useState<AntragForm | null>(null);
  const [sende, setSende] = useState(false);

  const sonntag = new Date(wochenStart);
  sonntag.setDate(sonntag.getDate() + 6);

  const laden = useCallback(async () => {
    setLoading(true);
    const von = iso(wochenStart);
    const bis = iso(sonntag);
    try {
      const [res, e, a] = await Promise.all([
        zeiterfassungService.meine(von, bis),
        zeiterfassungService.meineEintraege(von, bis).catch(() => [] as ZeiterfassungEintrag[]),
        zeiterfassungService.meineAntraege(von, bis).catch(() => [] as Korrekturantrag[]),
      ]);
      setData(res);
      setEintraege(e);
      setAntraege(a);
    } catch {
      toast.error('Arbeitszeiten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wochenStart]);

  useEffect(() => { laden(); }, [laden]);

  const verschiebe = (wochen: number) => {
    const d = new Date(wochenStart);
    d.setDate(d.getDate() + wochen * 7);
    setWochenStart(montag(d));
    setOffenerTag(null);
    setForm(null);
  };

  const toggleTag = (datum: string) => {
    setOffenerTag((cur) => (cur === datum ? null : datum));
    setForm(null);
  };

  const formOeffnen = (t: TagesAuswertung) => {
    // Sinnvoller Vorschlag: fehlt die Abmeldung -> GEHEN, sonst KOMMEN
    const typ: StempelTyp = t.offen ? 'GEHEN' : 'KOMMEN';
    setForm({ typ, uhrzeit: typ === 'GEHEN' ? '16:00' : '08:00', begruendung: '' });
  };

  const antragSenden = async (datum: string) => {
    if (!form) return;
    if (!form.uhrzeit) { toast.error('Bitte eine Uhrzeit angeben'); return; }
    if (!form.begruendung.trim()) { toast.error('Bitte eine kurze Begründung angeben'); return; }
    setSende(true);
    try {
      await zeiterfassungService.antragStellen({ typ: form.typ, zeitpunkt: `${datum} ${form.uhrzeit}`, begruendung: form.begruendung.trim() });
      toast.success('Nachtrag beantragt – der Admin prüft ihn');
      markiereNeu(`az-tag-${datum}`);
      setForm(null);
      await laden();
    } catch (e: any) {
      toast.error(fehlerText(e, 'Antrag konnte nicht gesendet werden'));
    } finally {
      setSende(false);
    }
  };

  const antragZurueckziehen = async (id: number) => {
    if (!window.confirm('Nachtrag zurückziehen?')) return;
    try {
      await zeiterfassungService.antragZurueckziehen(id);
      toast.success('Nachtrag zurückgezogen');
      await laden();
    } catch (e: any) {
      toast.error(fehlerText(e, 'Nachtrag konnte nicht zurückgezogen werden'));
    }
  };

  const offeneAntraegeGesamt = antraege.filter((a) => a.status === 'OFFEN').length;

  return (
    <div className="p-4 md:p-6">
      <PageHeader title="Meine Arbeitszeiten" subtitle="Ein-/Ausstempeln per QR-Code am Terminal. Tag antippen für Details und Nachträge." />

      {/* Wochen-Navigation */}
      <div className="bg-white rounded-lg shadow p-3 mb-4 flex items-center justify-between">
        <button onClick={() => verschiebe(-1)} className="px-3 py-1.5 rounded-lg hover:bg-gray-100" aria-label="Vorige Woche"><Icon.ChevronLeft className="h-5 w-5" /></button>
        <div className="text-sm font-medium text-gray-700">
          {wochenStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – {sonntag.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </div>
        <button onClick={() => verschiebe(1)} className="px-3 py-1.5 rounded-lg hover:bg-gray-100" aria-label="Nächste Woche"><Icon.ChevronRight className="h-5 w-5" /></button>
      </div>

      {/* Summen */}
      {data && (
        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4">
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <p className="text-xs text-gray-500">Ist (Woche)</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900">{fmt(data.summeNettoMinuten)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <p className="text-xs text-gray-500">Soll (Woche)</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900">{fmt(data.summeSollMinuten)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-3 md:p-4">
            <p className="text-xs text-gray-500">Saldo</p>
            <p className={`text-lg md:text-2xl font-bold ${saldoColor(data.saldoMinuten)}`}>{fmtSaldo(data.saldoMinuten)}</p>
          </div>
        </div>
      )}

      {offeneAntraegeGesamt > 0 && (
        <div className="mb-4 text-sm bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg p-3">
          {offeneAntraegeGesamt === 1 ? 'Ein Nachtrag wartet' : `${offeneAntraegeGesamt} Nachträge warten`} auf Freigabe durch den Admin. Der Saldo berücksichtigt sie erst nach der Genehmigung.
        </div>
      )}

      {loading ? (
        <LoadingScreen />
      ) : !data || data.tage.length === 0 ? (
        <div className="bg-white rounded-lg shadow"><EmptyState icon={<Icon.Uhr className="h-6 w-6" />} title="Keine Einträge in dieser Woche" hint="Stempeln Sie am Terminal per QR-Code ein." /></div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {data.tage.map((t) => {
            const badge = statusBadge(t);
            const tagEintraege = eintraege.filter((e) => e.zeitpunkt.startsWith(t.datum)).sort((a, b) => a.zeitpunkt.localeCompare(b.zeitpunkt));
            const tagAntraege = antraege.filter((a) => a.zeitpunkt.startsWith(t.datum));
            const offenerAntrag = tagAntraege.some((a) => a.status === 'OFFEN');
            const istOffen = offenerTag === t.datum;
            const glow = ankunft(`az-tag-${t.datum}`);
            return (
              <div key={t.datum} ref={glow.ref} className={glow.className}>
                <button
                  onClick={() => toggleTag(t.datum)}
                  className={`w-full text-left p-3 md:p-4 flex items-center gap-3 hover:bg-gray-50 ${istOffen ? 'bg-gray-50' : ''}`}
                >
                  <div className="w-12 text-center flex-shrink-0">
                    <div className="text-sm font-semibold text-gray-900">{t.wochentag}</div>
                    <div className="text-xs text-gray-400">{t.datum.slice(8)}.{t.datum.slice(5, 7)}.</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900">
                      {t.kommen ? `${t.kommen} – ${t.gehen ?? '…'}` : null}
                      {t.pauseMinuten > 0 && <span className="text-xs text-gray-400 ml-2">(−{t.pauseMinuten}m Pause)</span>}
                    </div>
                    {badge && <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
                    {t.bearbeitet && <span className="inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"><Icon.Edit className="inline h-3 w-3 mr-1 align-[-1px]" />korrigiert</span>}
                    {offenerAntrag && <span className="inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Antrag offen</span>}
                    {t.warnungen.map((w, i) => (
                      <p key={i} className="text-xs text-amber-600 mt-0.5 flex items-center gap-1"><Icon.Warnung className="h-3.5 w-3.5 flex-shrink-0" />{w}</p>
                    ))}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-gray-900">{fmt(t.nettoMinuten + t.gutschriftMinuten)}</div>
                    <div className="text-xs text-gray-400">Soll {fmt(t.sollMinuten)}</div>
                    <div className={`text-xs font-medium ${saldoColor(t.saldoMinuten)}`}>{fmtSaldo(t.saldoMinuten)}</div>
                  </div>
                  <span className="text-gray-300 flex-shrink-0">{istOffen ? <Icon.ChevronUp className="h-4 w-4" /> : <Icon.ChevronDown className="h-4 w-4" />}</span>
                </button>

                {istOffen && (
                  <div className="ui-aufklappen px-3 md:px-4 pb-4 pt-1 bg-gray-50 border-t border-gray-100">
                    {/* Rohstempel des Tages */}
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Verlauf</p>
                    {tagEintraege.length === 0 ? (
                      <p className="text-sm text-gray-400">Keine Stempel an diesem Tag.</p>
                    ) : (
                      <ol className="space-y-1">
                        {tagEintraege.map((e) => (
                          <li key={e.id} className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-gray-900 w-12">{e.zeitpunkt.slice(11)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${e.typ === 'KOMMEN' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {e.typ === 'KOMMEN' ? 'Kommen' : 'Gehen'}
                            </span>
                            <span className="text-xs text-gray-400">{quelleLabel[e.quelle] ?? e.quelle}</span>
                            {e.notiz && <span className="text-xs text-gray-500 truncate" title={e.notiz}>· {e.notiz}</span>}
                          </li>
                        ))}
                      </ol>
                    )}

                    {/* Anträge des Tages */}
                    {tagAntraege.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 mb-1.5">Nachträge</p>
                        <ul className="space-y-1">
                          {tagAntraege.map((a) => {
                            const b = antragStatusBadge[a.status];
                            return (
                              <li key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-mono text-gray-900 w-12">{a.zeitpunkt.slice(11)}</span>
                                <span className="text-gray-700">{a.typ === 'KOMMEN' ? 'Kommen' : 'Gehen'}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                                <span className="text-xs text-gray-500 truncate">· {a.begruendung}</span>
                                {a.status === 'ABGELEHNT' && a.ablehnungsGrund && <span className="text-xs text-red-600">({a.ablehnungsGrund})</span>}
                                {a.status === 'OFFEN' && (
                                  <button onClick={() => antragZurueckziehen(a.id)} className="text-xs text-gray-500 hover:text-red-600 underline">zurückziehen</button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Nachtrag beantragen */}
                    {!form ? (
                      <button
                        onClick={() => formOeffnen(t)}
                        className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50"
                      >
                        <Icon.Plus className="h-3.5 w-3.5" />Stempel nachtragen
                      </button>
                    ) : (
                      <div className="ui-aufklappen mt-3 bg-white border border-gray-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-900 mb-2">Vergessenen Stempel nachtragen</p>
                        <p className="text-xs text-gray-500 mb-3">Der Vorschlag wird vom Admin geprüft und erst nach Genehmigung gebucht.</p>
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">Art</label>
                            <select value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value as StempelTyp })} className="p-2 border border-gray-300 rounded-lg text-sm">
                              <option value="KOMMEN">Kommen</option>
                              <option value="GEHEN">Gehen</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Uhrzeit</label>
                            <UhrzeitFeld value={form.uhrzeit} onChange={(v) => setForm({ ...form, uhrzeit: v })} className="p-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-medium mb-1">Begründung</label>
                            <input
                              value={form.begruendung}
                              onChange={(e) => setForm({ ...form, begruendung: e.target.value })}
                              maxLength={255}
                              placeholder="z. B. Ausstempeln vergessen"
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => antragSenden(t.datum)} disabled={sende} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm disabled:opacity-50">
                            {sende ? 'Sende…' : 'Antrag senden'}
                          </button>
                          <button onClick={() => setForm(null)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-sm">Abbrechen</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Arbeitszeiten;
